"use client";

import { useCallback, useSyncExternalStore } from "react";

const FAVORITES_KEY = "konakla_favorites";
const FAVORITES_EVENT = "konakla:favorites-change";

const EMPTY_LIST: string[] = [];

function readStore(): string[] {
  if (typeof window === "undefined") return EMPTY_LIST;
  try {
    const raw = window.localStorage.getItem(FAVORITES_KEY);
    return raw ? (JSON.parse(raw) as string[]) : EMPTY_LIST;
  } catch (err) {
    console.error("Favoriler yüklenemedi:", err);
    return EMPTY_LIST;
  }
}

// Aynı referansın dönmesini garantilemek için cache — useSyncExternalStore
// getSnapshot'ın referansını koruması gerekir, aksi halde sonsuz render olur.
let cachedSnapshot: string[] = EMPTY_LIST;
let cachedRaw: string | null = null;

function getSnapshot(): string[] {
  if (typeof window === "undefined") return EMPTY_LIST;
  const raw = window.localStorage.getItem(FAVORITES_KEY);
  if (raw === cachedRaw) return cachedSnapshot;
  cachedRaw = raw;
  cachedSnapshot = raw ? (JSON.parse(raw) as string[]) : EMPTY_LIST;
  return cachedSnapshot;
}

function getServerSnapshot(): string[] {
  return EMPTY_LIST;
}

function subscribe(onChange: () => void): () => void {
  const handler = () => onChange();
  window.addEventListener(FAVORITES_EVENT, handler);
  window.addEventListener("storage", handler);
  return () => {
    window.removeEventListener(FAVORITES_EVENT, handler);
    window.removeEventListener("storage", handler);
  };
}

function writeStore(next: string[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(FAVORITES_KEY, JSON.stringify(next));
    window.dispatchEvent(new Event(FAVORITES_EVENT));
  } catch (err) {
    console.error("Favoriler kaydedilemedi:", err);
  }
}

/**
 * LocalStorage tabanlı favoriler hook'u. React 19 uyumlu external-store deseni.
 * Kullanıcı giriş yapmadan da favorilerini saklayabilir.
 */
export function useFavorites() {
  const favorites = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const addFavorite = useCallback((listingId: string) => {
    const current = readStore();
    if (current.includes(listingId)) return;
    writeStore([...current, listingId]);
  }, []);

  const removeFavorite = useCallback((listingId: string) => {
    const current = readStore();
    writeStore(current.filter((id) => id !== listingId));
  }, []);

  const toggleFavorite = useCallback(
    (listingId: string) => {
      if (favorites.includes(listingId)) {
        removeFavorite(listingId);
      } else {
        addFavorite(listingId);
      }
    },
    [favorites, addFavorite, removeFavorite]
  );

  const isFavorite = useCallback((listingId: string) => favorites.includes(listingId), [favorites]);

  return {
    favorites,
    // Geriye uyumluluk için: component'ler hydration guard amacıyla isLoaded'i
    // kullanıyor olabilir. useSyncExternalStore SSR'da daima getServerSnapshot
    // sonucunu döndüğü için client mount sonrası değer zaten doğru olur; bu
    // değer always-true tutulur, flicker'ı önlemek için component'ler
    // client-only render etmek istiyorsa useIsClient vs kullanabilir.
    isLoaded: true,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    isFavorite,
    favoritesCount: favorites.length,
  };
}
